import * as React from 'react';

/**
 * Who owns the global search palette's open state: `AppLayout` provides it, the top bar's `GlobalSearch`
 * reads and writes it, and any screen can open the palette through `useSearchPalette()?.openSearch()` (the
 * 404 offers "Search ⌘K"). Outside the layout (a test mounting one screen, the bare layout) the hook
 * returns null and `GlobalSearch` keeps its own state.
 */
export interface SearchPaletteControl {
  open: boolean;
  setOpen(open: boolean): void;
  openSearch(): void;
}

const SearchPaletteContext = React.createContext<SearchPaletteControl | null>(null);

export function SearchPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const value = React.useMemo(() => ({ open, setOpen, openSearch: () => setOpen(true) }), [open]);
  return <SearchPaletteContext.Provider value={value}>{children}</SearchPaletteContext.Provider>;
}

export function useSearchPalette(): SearchPaletteControl | null {
  return React.useContext(SearchPaletteContext);
}

/** The shortcut as the keyboard prints it: Cmd+K on a Mac, Ctrl+K elsewhere. */
export function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

export function searchShortcut(): string {
  return isMac() ? '⌘K' : 'Ctrl K';
}
